import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberDto, ProjectDto, TagDto } from '../api/types';
import {
  useDeleteTag,
  useInviteMember,
  useMe,
  usePatchPrefs,
  useProjects,
  useSetProjectShares,
  useTags,
  useUpdateProject,
  useUpdateTag
} from '../hooks/queries';
import { ModalPortal } from './ModalPortal';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { Avatar } from './Avatar';
import { IconLock, IconTrash } from './icons';

interface SettingsDialogProps {
  onClose: () => void;
}

/**
 * Workspace settings: per-project active/inactive switches (inactive projects
 * vanish from the sidebar and from all cross-project views) and user prefs.
 */
export function SettingsDialog({ onClose }: SettingsDialogProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const me = useMe();
  const projects = useProjects(workspace.id);
  const updateProject = useUpdateProject(workspace.id);
  const patchPrefs = usePatchPrefs();
  const tags = useTags(workspace.id);
  const updateTag = useUpdateTag(workspace.id);
  const deleteTag = useDeleteTag(workspace.id);
  const setShares = useSetProjectShares(workspace.id);
  const invite = useInviteMember(workspace.id);
  const { members } = useWorkspace();
  const [inviteEmail, setInviteEmail] = useState('');
  const isOwner = workspace.role === 'OWNER';
  const usedSlots = members.length + workspace.pendingInvites;

  const submitInvite = (event: FormEvent) => {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email || invite.isPending) return;
    invite.mutate(email, { onSuccess: () => setInviteEmail('') });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sorted = useMemo(
    () =>
      [...(projects.data ?? [])].sort(
        (a, b) =>
          Number(a.archived) - Number(b.archived) ||
          a.position - b.position ||
          a.name.localeCompare(b.name)
      ),
    [projects.data]
  );

  const useTagsPref = me.data?.prefs?.useTags !== false;
  const sortedTags = useMemo(
    () => [...(tags.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tags.data]
  );

  return (
    <ModalPortal>
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal settings-modal">
        <h2>{t('settings.title')}</h2>

        <div className="modal-field">
          <span className="kicker">{t('settings.members')}</span>
          <div className="modal-hint">
            {t('settings.familyPlan', { plan: workspace.plan, used: usedSlots, max: workspace.maxSlots })}
          </div>
          <div className="settings-projects">
            {members.map((member: MemberDto) => (
              <div key={member.id} className="settings-project settings-member">
                <Avatar name={member.displayName || member.email || '?'} />
                <span className="settings-project-name">
                  {member.displayName || member.email}
                </span>
                {member.role === 'OWNER' && (
                  <span className="settings-project-badge">{t('settings.owner')}</span>
                )}
              </div>
            ))}
          </div>
          {workspace.pendingInvites > 0 && (
            <div className="modal-hint">
              {t('settings.pendingInvites', { count: workspace.pendingInvites })}
            </div>
          )}
          {isOwner && usedSlots < workspace.maxSlots && (
            <form className="settings-invite" onSubmit={submitInvite}>
              <input
                className="field-input"
                type="email"
                value={inviteEmail}
                placeholder={t('settings.invitePlaceholder')}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!inviteEmail.trim() || invite.isPending}
              >
                {t('settings.invite')}
              </button>
            </form>
          )}
          {invite.isError && <div className="modal-hint">{t('settings.inviteError')}</div>}
        </div>

        <div className="modal-field">
          <span className="kicker">{t('settings.general')}</span>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={useTagsPref}
              onChange={(e) => patchPrefs.mutate({ useTags: e.target.checked })}
            />
            <span>{t('settings.useTags')}</span>
          </label>
          <div className="modal-hint">{t('settings.useTagsHint')}</div>

          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={me.data?.prefs?.knowledgeEnabled === true}
              onChange={(e) => patchPrefs.mutate({ knowledgeEnabled: e.target.checked })}
            />
            <span>{t('settings.knowledgeModule')}</span>
          </label>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={me.data?.prefs?.checkinsEnabled === true}
              onChange={(e) => patchPrefs.mutate({ checkinsEnabled: e.target.checked })}
            />
            <span>{t('settings.checkinsModule')}</span>
          </label>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={me.data?.prefs?.habitsEnabled === true}
              onChange={(e) => patchPrefs.mutate({ habitsEnabled: e.target.checked })}
            />
            <span>{t('settings.habitsModule')}</span>
          </label>
          <div className="modal-hint">{t('settings.knowledgeModuleHint')}</div>

          {(() => {
            const reminder = (me.data?.prefs?.checkinReminders ?? {}) as {
              daily?: { enabled?: boolean; time?: string };
            };
            const enabled = reminder.daily?.enabled === true;
            const time = reminder.daily?.time ?? '06:45';
            return (
              <>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) =>
                      patchPrefs.mutate({
                        checkinReminders: { daily: { enabled: e.target.checked, time } },
                      })
                    }
                  />
                  <span>{t('settings.checkinReminder')}</span>
                </label>
                {enabled && (
                  <input
                    type="time"
                    className="settings-time-input"
                    value={time}
                    onChange={(e) =>
                      patchPrefs.mutate({
                        checkinReminders: { daily: { enabled: true, time: e.target.value } },
                      })
                    }
                  />
                )}
                <div className="modal-hint">{t('settings.checkinReminderHint')}</div>
              </>
            );
          })()}

          <a className="settings-setup-link" href={"/setup.html?v=" + (process.env.BUILD_ID ?? "")} target="_blank" rel="noreferrer">
            {t('settings.setupLink')}
          </a>
          <div className="modal-hint">{t('settings.setupLinkHint')}</div>
        </div>

        {useTagsPref && (
          <div className="modal-field">
            <span className="kicker">{t('settings.tags')}</span>
            <div className="modal-hint">{t('settings.tagsHint')}</div>
            <div className="settings-projects">
              {sortedTags.length === 0 && (
                <div className="modal-hint">{t('detail.noTags')}</div>
              )}
              {sortedTags.map((tag) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  onRename={(name) => updateTag.mutate({ tagId: tag.id, patch: { name } })}
                  onDelete={() => deleteTag.mutate(tag.id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="modal-field">
          <span className="kicker">{t('settings.projects')}</span>
          <div className="modal-hint">{t('settings.projectsHint')}</div>
          <div className="settings-projects">
            {sorted.map((project) => {
              const mine = project.ownerUserId === me.data?.id;
              const others = members.filter((m) => m.id !== me.data?.id);
              return (
                <div
                  key={project.id}
                  className={`settings-project${project.archived ? ' archived' : ''}`}
                >
                  <input
                    type="checkbox"
                    disabled={!mine}
                    checked={!project.archived}
                    onChange={(e) =>
                      updateProject.mutate({
                        projectId: project.id,
                        patch: { archived: !e.target.checked }
                      })
                    }
                  />
                  <span className="color-dot" style={{ background: project.color }} />
                  <span className="settings-project-name">{project.name}</span>
                  {!mine && (
                    <span className="settings-project-badge">
                      {t('settings.sharedBy', {
                        name: memberLabel(members, project.ownerUserId)
                      })}
                    </span>
                  )}
                  {mine &&
                    others.map((member) => {
                      const shared = project.sharedWith.includes(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          className={`settings-share-chip${shared ? ' on' : ''}`}
                          title={
                            (shared ? t('settings.unshareWith') : t('settings.shareWith')) +
                            ' ' +
                            (member.displayName || member.email)
                          }
                          onClick={() =>
                            setShares.mutate({
                              projectId: project.id,
                              userIds: shared
                                ? project.sharedWith.filter((id) => id !== member.id)
                                : [...project.sharedWith, member.id]
                            })
                          }
                        >
                          {(member.displayName || member.email || '?').charAt(0).toUpperCase()}
                        </button>
                      );
                    })}
                  {project.archived && (
                    <span className="settings-project-badge">{t('settings.inactive')}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

function memberLabel(members: MemberDto[], userId: string | null): string {
  const member = members.find((m) => m.id === userId);
  return member?.displayName || member?.email || '?';
}

interface TagRowProps {
  tag: TagDto;
  onRename: (name: string) => void;
  onDelete: () => void;
}

function TagRow({ tag, onRename, onDelete }: TagRowProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(tag.name);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => setName(tag.name), [tag.name]);

  const commit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(tag.name);
      return;
    }
    if (trimmed !== tag.name) onRename(trimmed);
  };

  return (
    <div className="settings-project settings-tag-row">
      <span className="color-dot" style={{ background: tag.color }} />
      <input
        className="settings-tag-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <button
        type="button"
        className="icon-btn"
        style={confirming ? { color: 'var(--danger)' } : undefined}
        onClick={() => (confirming ? onDelete() : setConfirming(true))}
        onBlur={() => setConfirming(false)}
        aria-label={t('settings.deleteTag')}
        title={confirming ? t('settings.confirmDeleteTag') : t('settings.deleteTag')}
      >
        <IconTrash />
      </button>
    </div>
  );
}
